import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Shield, Server, Network, Users, Clock, Award, ArrowRight, CheckCircle, Phone, Mail } from "lucide-react";
const Index = () => {
  const services = [{
    icon: <Network className="h-8 w-8" />,
    title: "Network Infrastructure",
    description: "Complete network design, implementation, and management solutions for businesses of all sizes.",
    features: ["Network Design", "Implementation", "24/7 Monitoring"]
  }, {
    icon: <Shield className="h-8 w-8" />,
    title: "Cybersecurity Solutions",
    description: "Comprehensive security services to protect your business from evolving cyber threats.",
    features: ["Threat Assessment", "Security Audits", "Incident Response"]
  }, {
    icon: <Server className="h-8 w-8" />,
    title: "IT Infrastructure",
    description: "End-to-end technology infrastructure consulting and implementation services.",
    features: ["Server Management", "Cloud Migration", "System Integration"]
  }, {
    icon: <Users className="h-8 w-8" />,
    title: "Managed IT Services",
    description: "Proactive IT support and management to keep your business running smoothly.",
    features: ["Help Desk Support", "Remote Monitoring", "Preventive Maintenance"]
  }];
  const stats = [{
    number: "20+",
    label: "Years of Experience"
  }, {
    number: "500+",
    label: "Clients Served"
  }, {
    number: "7 Days",
    label: "A Week Support"
  }, {
    number: "99.9%",
    label: "Uptime Guarantee"
  }];
  return <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Navigation currentPage="" />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <Badge className="mb-4" variant="secondary">
                <Award className="h-4 w-4 mr-2" />
                Over 20 Years of Excellence
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-foreground mb-6">
                Comprehensive IT Solutions for 
                <span className="bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                  Modern Business
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                SAMAC Technology & Networking Solutions delivers end-to-end technology infrastructure, 
                consulting, and sales services to businesses and government agencies across Mississippi and beyond.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg hover-lift">
                  <Link to="/contact" className="flex items-center">
                    Get Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="hover-lift">
                  <Link to="/services">View Our Services</Link>
                </Button>
              </div>
            </div>
            <div className="relative animate-slide-up">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl hover-lift">
                <img src="https://images.unsplash.com/photo-1646153114001-495dfb56506d?w=600&auto=format&fit=crop&q=80" alt="Modern technology office workspace" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl animate-scale">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 p-2 rounded-full">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Durant, Mississippi</p>
                    <p className="text-sm text-muted-foreground">Serving Local & Enterprise Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => <div key={index} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-primary-foreground/80">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Core Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From small local businesses to large-scale enterprise organizations and government agencies, 
              we deliver tailored technology solutions that drive success.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => <Card key={index} className="group hover:shadow-xl hover-lift transition-all duration-300 border-0 shadow-md animate-fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                <CardHeader>
                  <div className="bg-primary/10 p-3 rounded-lg w-fit mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {service.icon}
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => <li key={idx} className="flex items-center text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>)}
                  </ul>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-r from-secondary/50 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Why Choose SAMAC Technology?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Two Decades of Experience</h3>
                    <p className="text-muted-foreground">Over 20 years of proven expertise in delivering technology solutions across diverse industries.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Diverse Clientele</h3>
                    <p className="text-muted-foreground">Serving everyone from small local businesses to large enterprise organizations and government agencies.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">End-to-End Solutions</h3>
                    <p className="text-muted-foreground">Complete technology infrastructure, consulting, and sales services under one roof.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1581091877018-dac6a371d50f?w=600&auto=format&fit=crop&q=80" alt="Professional IT team collaboration" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Transform Your Technology Infrastructure?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Let our experienced team help you leverage technology to drive your business forward. 
            Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
              <Link to="/contact" className="flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                Call Us Today
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              <Link to="/contact" className="flex items-center">
                <Mail className="mr-2 h-5 w-5" />
                Email Consultation
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default Index;